import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Home, Heart, CreditCard, Gift, MessageCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";

const navItems = [
  { path: "/", label: "Home", icon: Home, requiresAuth: false },
  { path: "/wishlist", label: "Wishlist", icon: Heart, requiresAuth: true },
];

const navItemsRight = [
  { path: "/card", label: "Card", icon: CreditCard, requiresAuth: true },
  { path: "/ricompense", label: "Ricompense", icon: Gift, requiresAuth: true },
];

const BottomNavigation = React.forwardRef<HTMLElement, Record<string, never>>(
  (_, ref) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useAuth();

    const handleNavClick = (e: React.MouseEvent, path: string, requiresAuth: boolean) => {
      if (requiresAuth && !user) {
        e.preventDefault();
        navigate('/login', { state: { from: { pathname: path } } });
      }
    };

    const renderNavItem = (item: typeof navItems[0]) => {
      const isActive = location.pathname === item.path;
      const Icon = item.icon;
      
      return (
        <NavLink
          key={item.path}
          to={item.path}
          onClick={(e) => handleNavClick(e, item.path, item.requiresAuth)}
          className={cn(
            "flex items-center justify-center transition-all duration-200 relative",
            isActive 
              ? "w-12 h-12" 
              : "w-10 h-10"
          )}
        >
          {isActive && (
            <span 
              className="absolute inset-0 rounded-full bg-card"
              style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' }}
            />
          )}
          <Icon 
            className={cn(
              "w-5 h-5 transition-colors duration-200 relative z-10",
              "text-foreground"
            )} 
            strokeWidth={isActive ? 2.5 : 1.5}
          />
        </NavLink>
      );
    };

    return (
      <nav ref={ref} className="fixed left-1/2 -translate-x-1/2 z-50" style={{ bottom: 'calc(var(--safe-area-bottom, 0px) + 12px)' }}>
        <div 
          className="px-5 py-4 rounded-full max-w-sm mx-auto flex items-center justify-center gap-4"
          style={{
            background: 'rgba(255, 255, 255, 0.65)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
            height: '68px',
          }}
        >
          {/* Left nav items */}
          {navItems.map(renderNavItem)}
          
          {/* Concierge button - center */}
          <NavLink
            to="/concierge"
            className={cn(
              "flex items-center justify-center transition-all duration-200 active:scale-95 relative rounded-full w-12 h-12",
            )}
            style={{
              background: 'linear-gradient(135deg, #B39678 0%, #C4A98A 50%, #B39678 100%)',
              boxShadow: '0 4px 16px -2px rgba(179, 150, 120, 0.35)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
            }}
            aria-label="Apri Concierge"
          >
            <MessageCircle className="w-5 h-5 text-white" />
          </NavLink>
          
          {/* Right nav items */}
          {navItemsRight.map(renderNavItem)}
        </div>
      </nav>
    );
  }
);

BottomNavigation.displayName = "BottomNavigation";

export { BottomNavigation };
